import { useApolloClient } from "@apollo/client/react";
import { get } from "lodash";
import { useCallback, useMemo } from "react";
import isUnknownFieldError from "src/util/isUnknownFieldError";

import {
  createDesign as createDesignMutation,
  deleteDesign as deleteDesignMutation,
  getDesign as getDesignQuery,
  getDesignForWorkspace as getDesignForWorkspaceQuery,
  designTypeSourceWorkspaces as designTypeSourceWorkspacesQuery,
  designTypes as designTypesQuery,
  designWorkflowStages as designWorkflowStagesQuery,
  listDesigns as listDesignsQuery,
  updateDesign as updateDesignMutation,
} from "./designs.graphql";
import {
  createDesignOption as createDesignOptionMutation,
  deleteDesignOption as deleteDesignOptionMutation,
  updateDesignOption as updateDesignOptionMutation,
} from "./overlay/designOption.graphql";
import {
  buildDesignMutationInput,
  type DesignMutationFormInput,
} from "./utils";
import type { DeprovisioningPayload } from "./designDeprovisionConsts";
import {
  attachDesignTypeSources,
  type Design,
  type DesignMetaOption,
} from "./designUtils";

/** A design option row as returned by the overlay option mutations. */
export type DesignOptionRecord = {
  id: string;
  designId: string;
  name: string;
  isTrunk: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type DeleteDesignResult = {
  deleted: boolean;
  accepted: boolean;
  designId?: string | null;
  deprovisioning?: DeprovisioningPayload | null;
};

export const useDesignsRepository = () => {
  const client = useApolloClient();

  const listDesigns = useCallback(async (): Promise<Design[]> => {
    const { data } = await client.query({
      query: listDesignsQuery,
      fetchPolicy: "network-only",
    });
    return get(data, "designs", []);
  }, [client]);

  const getDesign = useCallback(
    async ({ id }: { id: string }): Promise<Design | null> => {
      const { data } = await client.query({
        query: getDesignQuery,
        variables: { id },
        fetchPolicy: "network-only",
      });
      return get(data, "design", null);
    },
    [client]
  );

  const getDesignForWorkspace =
    useCallback(async (): Promise<Design | null> => {
      const { data } = await client.query({
        query: getDesignForWorkspaceQuery,
        fetchPolicy: "network-only",
      });
      return get(data, "design", null);
    }, [client]);

  const createDesign = useCallback(
    async ({
      input,
    }: {
      input: DesignMutationFormInput;
    }): Promise<Design | null> => {
      const { data } = await client.mutate({
        mutation: createDesignMutation,
        variables: { input: buildDesignMutationInput(input) },
      });
      return get(data, "design", null);
    },
    [client]
  );

  const updateDesign = useCallback(
    async ({
      id,
      input,
    }: {
      id: string;
      input: DesignMutationFormInput;
    }): Promise<Design | null> => {
      const { data } = await client.mutate({
        mutation: updateDesignMutation,
        variables: { id, input: buildDesignMutationInput(input) },
      });
      return get(data, "design", null);
    },
    [client]
  );

  const deleteDesign = useCallback(
    async ({ id }: { id: string }): Promise<DeleteDesignResult> => {
      const { data } = await client.mutate({
        mutation: deleteDesignMutation,
        variables: { id },
      });
      return {
        deleted: get(data, "deleteDesign.deleted", false),
        accepted: get(data, "deleteDesign.accepted", false),
        designId: get(data, "deleteDesign.designId", null),
        deprovisioning: get(data, "deleteDesign.deprovisioning", null),
      };
    },
    [client]
  );

  const getWorkflowStages = useCallback(async (): Promise<
    DesignMetaOption[]
  > => {
    const { data } = await client.query({
      query: designWorkflowStagesQuery,
    });
    return get(data, "designWorkflowStages", []);
  }, [client]);

  const getDesignTypes = useCallback(async (): Promise<DesignMetaOption[]> => {
    const { data } = await client.query({
      query: designTypesQuery,
    });
    const types = get(data, "designTypes", []) as DesignMetaOption[];
    // An API without the source field keeps the types, which then ask for a
    // baseline. Any other failure is thrown: dropping a type's source workspace
    // would show a baseline picker that the save then rejects.
    const source = await client
      .query({
        query: designTypeSourceWorkspacesQuery,
        errorPolicy: "all",
      })
      .catch((sourceError: unknown) => ({
        data: undefined,
        error: sourceError,
      }));
    if (source.error) {
      if (isUnknownFieldError(source.error)) return types;
      throw source.error;
    }
    return (
      attachDesignTypeSources(
        types,
        get(source.data, "designTypeSources", [])
      ) ?? types
    );
  }, [client]);

  const createDesignOption = useCallback(
    async ({
      id,
      name,
    }: {
      id: string;
      name: string;
    }): Promise<DesignOptionRecord | null> => {
      const { data } = await client.mutate({
        mutation: createDesignOptionMutation,
        variables: { id, input: { name } },
      });
      return get(data, "option", null);
    },
    [client]
  );

  const renameDesignOption = useCallback(
    async ({
      id,
      optionId,
      name,
    }: {
      id: string;
      optionId: string;
      name: string;
    }): Promise<DesignOptionRecord | null> => {
      const { data } = await client.mutate({
        mutation: updateDesignOptionMutation,
        variables: { id, optionId, input: { name } },
      });
      return get(data, "option", null);
    },
    [client]
  );

  const deleteDesignOption = useCallback(
    async ({
      id,
      optionId,
    }: {
      id: string;
      optionId: string;
    }): Promise<boolean> => {
      const { data } = await client.mutate({
        mutation: deleteDesignOptionMutation,
        variables: { id, optionId },
      });
      return get(data, "deleteDesignOption.deleted", false);
    },
    [client]
  );

  return useMemo(
    () => ({
      listDesigns,
      getDesign,
      getDesignForWorkspace,
      createDesign,
      updateDesign,
      deleteDesign,
      getWorkflowStages,
      getDesignTypes,
      createDesignOption,
      renameDesignOption,
      deleteDesignOption,
    }),
    [
      listDesigns,
      getDesign,
      getDesignForWorkspace,
      createDesign,
      updateDesign,
      deleteDesign,
      getWorkflowStages,
      getDesignTypes,
      createDesignOption,
      renameDesignOption,
      deleteDesignOption,
    ]
  );
};

export default useDesignsRepository;
